/**
 * PoolExceptionsView — l'écran « Parc partagé » du gestionnaire (démo CD31 du 2026-09-17).
 *
 * Trois onglets qui suivent le fil de la journée :
 *   - Maintenant  — ce qui est DEHORS (gardes en cours, retards, retours proches) ;
 *   - Planning    — de quoi je dispose (compteurs sur un créneau, ou la semaine) ;
 *   - À traiter   — ce qui a DÉJÀ dérapé (la chaîne d'écarts, avec son délai).
 *
 * Refonte du 2026-09-14 (retours JGS en répétition) : l'écran empilait quatre blocs
 * « en silo », et la liste d'écarts était brute. Il doit tenir à 900 véhicules et
 * 1 000 conducteurs — résumer d'abord, détailler l'exception, laisser le reste à un
 * clic. « Trop d'info tue l'info » : c'est le même défaut qu'aucune info, en pire,
 * parce que celui-là culpabilise. On aide le gestionnaire dans son travail, on ne lui
 * fait pas faire un travail.
 *
 * Le filtre U.T. et la recherche vivent ici et valent pour les trois onglets ; un
 * enfant peut resserrer le périmètre d'un clic (`onPickSite`). Rien ici ne vient de
 * la télématique — le SLA vient du serveur (`pool_exception.sla`).
 *
 * Contrat : docs/implementations/feature/POOL_POC_API_CONTRACT_2026-09-11.md §10.
 * `view_code = 'pool_exceptions'` est le contrat avec `app_views` (seed L6a,
 * `backend/scripts/seed_cd31_demo.mjs`) et NE CHANGE PAS avec le nom de l'écran.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, RefreshCw, Search, XCircle, Zap, CheckCircle2, Check,
  Car, User, Building2, Clock, X, ChevronDown, ChevronRight, CarFront,
} from 'lucide-react';
import { poolExceptionsAPI } from '../../api/pool.js';
import { useWorkingSites } from '../../index.js';
import { useFormat } from '../../i18n/format.js';
import SkeletonLoader from '../common/SkeletonLoader.jsx';
import PoolCustodyPanel from './PoolCustodyPanel.jsx';
import PoolAvailabilityPanel from './PoolAvailabilityPanel.jsx';
import PoolReserveForDriverDialog from './PoolReserveForDriverDialog.jsx';
import PoolScheduleGrid from './PoolScheduleGrid.jsx';

const TABS = ['now', 'planning', 'todo'];

/**
 * Filtre de statut de l'onglet « À traiter ». `active` = ouvert OU pris en compte :
 * c'est ce qui attend un geste, et c'est le défaut. Les résolus et expirés ne
 * s'affichent que sur demande — ils ne sont pas du travail.
 */
const STATUS_FILTERS = ['active', 'open', 'acknowledged', 'resolved', 'expired', 'all'];
const KIND_VALUES = [
  'late_return', 'early_return', 'no_show', 'defect_unreported',
  'key_not_returned', 'force_closed', 'vehicle_blocked', 'battery_low', 'vehicle_silent',
];

/** Au-delà de ce nombre d'écarts à traiter, seuls les groupes avec un dépassement s'ouvrent d'office. */
const EXPAND_ALL_BELOW = 12;

const STATUS_BADGE = {
  open: 'bg-red-100 text-red-800',
  acknowledged: 'bg-amber-100 text-amber-800',
  resolved: 'bg-emerald-100 text-emerald-800',
  expired: 'bg-gray-200 text-gray-700',
};

/** Urgence : dépassés d'abord, puis délai restant croissant, puis le plus ancien. */
export function sortByUrgency(a, b) {
  const aOverdue = !!a.sla?.overdue;
  const bOverdue = !!b.sla?.overdue;
  if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;
  const aRem = a.sla?.remaining_min;
  const bRem = b.sla?.remaining_min;
  if (aRem != null && bRem != null && aRem !== bRem) return aRem - bRem;
  if (aRem != null && bRem == null) return -1;
  if (aRem == null && bRem != null) return 1;
  return new Date(a.detected_at) - new Date(b.detected_at);
}

/**
 * Les écarts regroupés par nature, chaque groupe trié par urgence, les groupes
 * eux-mêmes par nombre de dépassés puis par taille. À 900 véhicules, « 7 retours
 * en retard » se lit ; sept lignes au milieu de quarante autres, non.
 * @returns {Array<{ kind: string, items: object[], overdue: number }>}
 */
export function groupExceptionsByKind(exceptions) {
  const byKind = new Map();
  for (const e of exceptions) {
    const list = byKind.get(e.kind) || [];
    list.push(e);
    byKind.set(e.kind, list);
  }
  return [...byKind.entries()]
    .map(([kind, items]) => ({
      kind,
      items: [...items].sort(sortByUrgency),
      overdue: items.filter((e) => e.status === 'open' && e.sla?.overdue).length,
    }))
    .sort((a, b) => b.overdue - a.overdue || b.items.length - a.items.length || a.kind.localeCompare(b.kind));
}

/** Un statut passe-t-il le filtre choisi ? `active` = ouvert ou pris en compte. */
export function matchesStatusFilter(status, filter) {
  if (filter === 'all') return true;
  if (filter === 'active') return status === 'open' || status === 'acknowledged';
  return status === filter;
}

/**
 * Une durée en minutes, lisible par un gestionnaire : « 45 min », « 22 h 31 », « 2 j 03 h ».
 * Mesuré en démo le 2026-09-12 : « Dépassé de 1351 min » ne se lit pas, « 22 h 31 » oui.
 */
function formatMinutes(min) {
  const m = Math.abs(Math.round(min ?? 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h < 24) return `${h} h ${String(r).padStart(2, '0')}`;
  const d = Math.floor(h / 24);
  return `${d} j ${String(h % 24).padStart(2, '0')} h`;
}

function ExceptionRow({ exception, onAcknowledge, onResolve, acting }) {
  const { t, fmt } = useFormat();
  const overdue = !!exception.sla?.overdue;
  const remaining = exception.sla?.remaining_min;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_BADGE[exception.status] || STATUS_BADGE.open}`}>
              {t(`poolExceptions.status.${exception.status}`, exception.status)}
            </span>
            {exception.status === 'open' && (
              overdue ? (
                <span className="flex items-center gap-1 text-red-600 text-sm font-medium">
                  <AlertTriangle className="w-4 h-4" />
                  {t('poolExceptions.row.overdueBy', { duration: formatMinutes(remaining) })}
                </span>
              ) : remaining != null ? (
                <span className="flex items-center gap-1 text-amber-600 text-sm">
                  <Clock className="w-4 h-4" />
                  {t('poolExceptions.row.remainingMin', { duration: formatMinutes(remaining) })}
                </span>
              ) : (
                <span className="text-gray-400 text-sm">{t('poolExceptions.row.noSla')}</span>
              )
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
            <span className="flex items-center gap-1">
              <Car className="w-3.5 h-3.5 text-gray-400" />
              {exception.vehicle_label || `#${exception.vehicle_id}`}
            </span>
            <span className="flex items-center gap-1">
              <User className="w-3.5 h-3.5 text-gray-400" />
              {exception.driver_name || t('poolExceptions.row.driverUnknown')}
            </span>
            <span className="flex items-center gap-1">
              <Building2 className="w-3.5 h-3.5 text-gray-400" />
              {exception.site_name || '—'}
            </span>
            <span className="text-gray-400">
              {t('poolExceptions.row.detectedAt', { time: fmt.dateTime(exception.detected_at) })}
            </span>
          </div>

          {exception.status === 'resolved' && exception.resolution_note && (
            <p className="mt-2 text-sm text-gray-500 italic">« {exception.resolution_note} »</p>
          )}

          {/* Q5 — chaque geste gestionnaire a un auteur (lot 1.5). */}
          {(exception.acknowledged_by_name || exception.resolved_by_name) && (
            <p className="mt-1 text-xs text-gray-400">
              {exception.resolved_by_name
                ? t('poolExceptions.row.resolvedBy', { name: exception.resolved_by_name })
                : t('poolExceptions.row.acknowledgedBy', { name: exception.acknowledged_by_name })}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {exception.status === 'open' && (
            <button
              onClick={() => onAcknowledge(exception)}
              disabled={acting}
              className="px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 disabled:opacity-50 flex items-center gap-1.5"
            >
              <Check className="w-4 h-4" />
              {t('poolExceptions.actions.acknowledge')}
            </button>
          )}
          {(exception.status === 'open' || exception.status === 'acknowledged') && (
            <button
              onClick={() => onResolve(exception)}
              disabled={acting}
              className="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              <CheckCircle2 className="w-4 h-4" />
              {t('poolExceptions.actions.resolve')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Un groupe d'écarts d'une même nature : l'en-tête dit combien, et combien sont
 * dépassés ; le corps ne s'affiche qu'ouvert. C'est ce qui permet de tenir quarante
 * écarts sur un écran sans que le gestionnaire les lise tous pour trouver les trois
 * qui pressent.
 */
function ExceptionGroup({ group, open, onToggle, actingId, onAcknowledge, onResolve }) {
  const { t } = useTranslation();
  return (
    <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50"
      >
        <span className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" /> : <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />}
          <span className="font-semibold text-gray-900 truncate">{t(`poolExceptions.kind.${group.kind}`, group.kind)}</span>
          <span className="text-sm text-gray-500">{t('poolExceptions.group.count', { count: group.items.length })}</span>
        </span>
        {group.overdue > 0 && (
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700">
            <AlertTriangle className="h-3.5 w-3.5" />
            {t('poolExceptions.group.overdue', { count: group.overdue })}
          </span>
        )}
      </button>
      {open && (
        <div className="space-y-2 border-t border-gray-100 p-3">
          {group.items.map((exception) => (
            <ExceptionRow
              key={exception.id}
              exception={exception}
              acting={actingId === exception.id}
              onAcknowledge={onAcknowledge}
              onResolve={onResolve}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ResolveModal({ exception, onClose, onSubmit, submitting, error }) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl shadow-2xl w-[480px] max-w-full">
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-white" />
            <h2 className="text-lg font-semibold text-white">{t('poolExceptions.resolveModal.title')}</h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-sm text-gray-600">
            {t(`poolExceptions.kind.${exception.kind}`, exception.kind)} — {exception.vehicle_label || `#${exception.vehicle_id}`}
          </p>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            placeholder={t('poolExceptions.resolveModal.notePlaceholder')}
          />
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            {t('poolExceptions.resolveModal.cancel')}
          </button>
          <button
            onClick={() => onSubmit(note)}
            disabled={submitting || !note.trim()}
            className="px-4 py-2 text-sm text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 flex items-center gap-1.5"
          >
            <Check className="w-4 h-4" />
            {t('poolExceptions.resolveModal.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Une pastille de compte sur un onglet : rouge quand ça presse, grise sinon. */
function TabBadge({ count, urgent }) {
  if (!count) return null;
  return (
    <span className={`ml-1.5 inline-flex min-w-[1.5rem] justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold ${urgent ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-700'}`}>
      {count}
    </span>
  );
}

function PoolExceptionsView() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: sites = [] } = useWorkingSites();

  const [tab, setTab] = useState('now');
  const [searchQuery, setSearchQuery] = useState('');
  const [siteFilter, setSiteFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [kindFilter, setKindFilter] = useState('all');
  const [actingId, setActingId] = useState(null);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveError, setResolveError] = useState(null);
  const [detecting, setDetecting] = useState(false);
  // Lot 1.6 (audit CD31, Q5) — réserver pour un agent sans smartphone.
  const [reserveOpen, setReserveOpen] = useState(false);
  // Préremplissage venu de la grille hebdomadaire : { vehicleId, start, end } ou null.
  const [reservePrefill, setReservePrefill] = useState(null);
  // « Compteurs » (panneau de disponibilité, par défaut) ou « Semaine » (grille).
  const [parkView, setParkView] = useState('counters');
  const [detectMessage, setDetectMessage] = useState(null);
  const [actionError, setActionError] = useState(null);
  // Groupes d'écarts ouverts à la main ; `null` = laisser le défaut décider.
  const [openGroups, setOpenGroups] = useState(null);

  // Les écarts se chargent quel que soit l'onglet : ils alimentent la pastille
  // « À traiter ». Un seul statut serveur ; `active` et `all` se tranchent ici.
  const serverStatus = statusFilter === 'active' || statusFilter === 'all' ? undefined : statusFilter;
  const queryKey = ['pool-exceptions', siteFilter, serverStatus, kindFilter];
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey,
    queryFn: () => poolExceptionsAPI.list({
      site_id: siteFilter !== 'all' ? siteFilter : undefined,
      status: serverStatus,
      kind: kindFilter !== 'all' ? kindFilter : undefined,
    }),
  });

  const exceptions = useMemo(() => data?.data || [], [data]);

  const filteredExceptions = useMemo(() => {
    let list = exceptions.filter((e) => matchesStatusFilter(e.status, statusFilter));
    if (searchQuery && searchQuery.length >= 2) {
      const q = searchQuery.toLowerCase();
      list = list.filter((e) =>
        e.vehicle_label?.toLowerCase().includes(q) ||
        e.driver_name?.toLowerCase().includes(q) ||
        e.site_name?.toLowerCase().includes(q));
    }
    return list;
  }, [exceptions, searchQuery, statusFilter]);

  const groups = useMemo(() => groupExceptionsByKind(filteredExceptions), [filteredExceptions]);

  const openCount = useMemo(() => exceptions.filter((e) => e.status === 'open').length, [exceptions]);
  const overdueCount = useMemo(() => exceptions.filter((e) => e.status === 'open' && e.sla?.overdue).length, [exceptions]);
  const resolvedTodayCount = useMemo(() => {
    const today = new Date().toDateString();
    return exceptions.filter((e) => e.status === 'resolved' && e.resolved_at && new Date(e.resolved_at).toDateString() === today).length;
  }, [exceptions]);

  // Défaut d'ouverture des groupes : tout ouvert quand c'est court, sinon seulement
  // ce qui a déjà dépassé son délai. Se recalcule quand la liste change de forme,
  // jusqu'à ce que le gestionnaire touche un groupe.
  const defaultOpen = useMemo(() => {
    const total = filteredExceptions.length;
    return new Set(groups.filter((g) => total <= EXPAND_ALL_BELOW || g.overdue > 0).map((g) => g.kind));
  }, [groups, filteredExceptions.length]);
  useEffect(() => { setOpenGroups(null); }, [siteFilter, statusFilter, kindFilter]);
  const effectiveOpen = openGroups ?? defaultOpen;
  const toggleGroup = (kind) => {
    const next = new Set(effectiveOpen);
    if (next.has(kind)) next.delete(kind); else next.add(kind);
    setOpenGroups(next);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['pool-exceptions'] });
    queryClient.invalidateQueries({ queryKey: ['pool-custody'] });
    queryClient.invalidateQueries({ queryKey: ['pool-availability'] });
    queryClient.invalidateQueries({ queryKey: ['pool-schedule'] });
  };

  const handleAcknowledge = async (exception) => {
    setActingId(exception.id);
    setActionError(null);
    try {
      await poolExceptionsAPI.acknowledge(exception.id);
      handleRefresh();
    } catch (err) {
      setActionError(t('poolExceptions.errors.actionFailed', { message: err.message }));
    } finally {
      setActingId(null);
    }
  };

  const handleResolveSubmit = async (note) => {
    if (!resolveTarget) return;
    setActingId(resolveTarget.id);
    setResolveError(null);
    try {
      await poolExceptionsAPI.resolve(resolveTarget.id, note);
      setResolveTarget(null);
      handleRefresh();
    } catch (err) {
      setResolveError(err.message || t('poolExceptions.errors.actionFailed', { message: err.message }));
    } finally {
      setActingId(null);
    }
  };

  const handleDetect = async () => {
    setDetecting(true);
    setDetectMessage(null);
    setActionError(null);
    try {
      const res = await poolExceptionsAPI.detect();
      setDetectMessage(t('poolExceptions.detectResult', {
        created: res?.data?.created ?? res?.created ?? 0,
        alreadyOpen: res?.data?.already_open ?? res?.already_open ?? 0,
      }));
      handleRefresh();
    } catch (err) {
      setActionError(t('poolExceptions.errors.actionFailed', { message: err.message }));
    } finally {
      setDetecting(false);
    }
  };

  // Un enfant resserre le périmètre d'un clic (puce d'U.T., ligne du tableau).
  const handlePickSite = (siteId) => setSiteFilter(siteId == null ? 'all' : String(siteId));

  if (isLoading) return <SkeletonLoader type="list" rows={6} />;

  // Les panneaux reçoivent `siteFilter` tel quel ('all' ou un id) : c'est sur 'all'
  // qu'ils décident d'afficher le bandeau / le tableau par U.T. Seul le dialogue de
  // réservation attend `null` (filtre de convenance).
  const siteIdForChildren = siteFilter !== 'all' ? siteFilter : null;
  const currentSite = siteFilter !== 'all' ? sites.find((s) => String(s.id) === String(siteFilter)) : null;

  return (
    <div className="p-6">
      {/* En-tête : le nom, le périmètre, la recherche — valables pour les trois onglets. */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <CarFront className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('poolExceptions.title')}</h1>
              <p className="text-gray-500 text-sm mb-1">{t('poolExceptions.subtitle')}</p>
              <p className="text-sm flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-red-600">{t('poolExceptions.stats.open', { count: openCount })}</span>
                <span className="text-gray-300">|</span>
                <span className="font-semibold text-orange-600">{t('poolExceptions.stats.overdue', { count: overdueCount })}</span>
                <span className="text-gray-300">|</span>
                <span className="font-semibold text-emerald-600">{t('poolExceptions.stats.resolvedToday', { count: resolvedTodayCount })}</span>
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <select
              value={siteFilter}
              onChange={(e) => setSiteFilter(e.target.value)}
              aria-label={t('poolExceptions.filters.site')}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="all">{t('poolExceptions.filters.siteAll')}</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>{s.site_name}</option>
              ))}
            </select>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('poolExceptions.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-56"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>

            <button
              onClick={handleRefresh}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title={t('common.actions.refresh')}
            >
              <RefreshCw className={`w-5 h-5 ${isFetching ? 'animate-spin' : ''}`} />
            </button>

            <button
              onClick={() => setReserveOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 whitespace-nowrap"
            >
              {t('poolExceptions.actions.reserveForDriver')}
            </button>
          </div>
        </div>

        {currentSite && (
          <p className="mt-3 text-sm text-gray-600">
            {t('poolExceptions.scope.site', { site: currentSite.site_name })}{' '}
            <button type="button" onClick={() => setSiteFilter('all')} className="text-blue-600 hover:underline">
              {t('poolExceptions.scope.widen')}
            </button>
          </p>
        )}

        {(actionError || error) && (
          <p className="mt-3 text-sm text-red-600">{actionError || t('poolExceptions.errors.loadFailed', { message: error?.message })}</p>
        )}
      </div>

      {/* Les trois onglets, avec ce qui presse en pastille. */}
      <div className="mb-4 flex flex-wrap gap-1 border-b border-gray-200" role="tablist">
        {TABS.map((key) => {
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(key)}
              className={`-mb-px flex items-center border-b-2 px-4 py-2.5 text-sm font-medium ${active ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900'}`}
            >
              {t(`poolExceptions.tabs.${key}`)}
              {key === 'todo' && <TabBadge count={openCount} urgent={overdueCount > 0} />}
            </button>
          );
        })}
      </div>

      {tab === 'now' && (
        <PoolCustodyPanel
          siteId={siteFilter}
          sites={sites}
          search={searchQuery}
          onChanged={handleRefresh}
          onPickSite={handlePickSite}
        />
      )}

      {tab === 'planning' && (
        <>
          <div className="mb-3 inline-flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm" role="tablist">
            {['counters', 'week'].map((view) => (
              <button
                key={view}
                type="button"
                role="tab"
                aria-selected={parkView === view}
                onClick={() => setParkView(view)}
                className={`rounded-md px-3 py-1.5 ${parkView === view ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                {t(view === 'counters' ? 'poolSchedule.viewList' : 'poolSchedule.viewWeek')}
              </button>
            ))}
          </div>
          {parkView === 'counters' ? (
            <PoolAvailabilityPanel
              siteId={siteFilter}
              sites={sites}
              search={searchQuery}
              onPickSite={handlePickSite}
            />
          ) : (
            <PoolScheduleGrid
              siteId={siteFilter}
              sites={sites}
              search={searchQuery}
              onReserveSlot={(slot) => { setReservePrefill(slot); setReserveOpen(true); }}
            />
          )}
        </>
      )}

      {tab === 'todo' && (
        <div>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label={t('poolExceptions.filters.status')}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            >
              {STATUS_FILTERS.map((s) => (
                <option key={s} value={s}>{t(`poolExceptions.statusFilter.${s}`)}</option>
              ))}
            </select>

            <select
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value)}
              aria-label={t('poolExceptions.filters.kind')}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="all">{t('poolExceptions.filters.kindAll')}</option>
              {KIND_VALUES.map((k) => (
                <option key={k} value={k}>{t(`poolExceptions.kind.${k}`)}</option>
              ))}
            </select>

            <button
              onClick={handleDetect}
              disabled={detecting}
              className="ml-auto px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2 whitespace-nowrap text-sm"
            >
              <Zap className="w-4 h-4" />
              {detecting ? t('poolExceptions.actions.detecting') : t('poolExceptions.actions.detectNow')}
            </button>
          </div>

          {detectMessage && (
            <p className="mb-3 text-sm text-emerald-600">{detectMessage}</p>
          )}

          {groups.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
              <p className="text-gray-500">{t(statusFilter === 'active' ? 'poolExceptions.emptyActive' : 'poolExceptions.empty')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {groups.map((group) => (
                <ExceptionGroup
                  key={group.kind}
                  group={group}
                  open={effectiveOpen.has(group.kind)}
                  onToggle={() => toggleGroup(group.kind)}
                  actingId={actingId}
                  onAcknowledge={handleAcknowledge}
                  onResolve={(exc) => { setResolveTarget(exc); setResolveError(null); }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {reserveOpen && (
        <PoolReserveForDriverDialog
          siteId={siteIdForChildren}
          initialVehicleId={reservePrefill?.vehicleId ?? null}
          initialStart={reservePrefill?.start ?? null}
          initialEnd={reservePrefill?.end ?? null}
          onClose={() => { setReserveOpen(false); setReservePrefill(null); }}
          onCreated={() => { setReserveOpen(false); setReservePrefill(null); handleRefresh(); }}
        />
      )}

      {resolveTarget && (
        <ResolveModal
          exception={resolveTarget}
          submitting={actingId === resolveTarget.id}
          error={resolveError}
          onClose={() => setResolveTarget(null)}
          onSubmit={handleResolveSubmit}
        />
      )}
    </div>
  );
}

export default PoolExceptionsView;
